import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { logError } from "@/lib/logger";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Briefcase, Trash, Users, MapPin, Star, User as UserIcon, Phone, Envelope } from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import { LOCATION_SUGGESTIONS, SALARY_SUGGESTIONS } from "@/lib/suggestions";
import { IN_CITIES } from "@/lib/indiaGeo";
import { smartPasteHandler } from "@/lib/smartPaste";

const empty = {
    title: "",
    client_id: "",
    hiring_manager_id: "",
    primary_location_id: "",
    primary_spoc_id: "",
    department: "",
    location: "",
    // V1.5.5.4 — work mode replaces the ad-hoc "Location" free-text field.
    work_mode: "Onsite",
    employment_type: "Full-time",
    seniority: "Mid",
    salary_range: "",
    description: "",
    requirements: "",
    status: "Open",
    screening_pass_threshold: 3.5,
    spocs: [],
    referral_eligible: false,
    referral_bounty_amount: "",
    referral_bounty_currency: "INR",
};

export default function JobsList() {
    const { user: me } = useAuth();
    const canWrite = ["admin", "recruiter", "hiring_manager", "superadmin"].includes(me?.role);
    // P0 RBAC — Recruiters can no longer create/edit/delete jobs. That's
    // an admin + hiring_manager privilege. `canWrite` above still governs
    // candidate/stage operations which recruiters retain.
    const canEditJob = ["admin", "hiring_manager", "superadmin"].includes(me?.role);
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [hiringManagers, setHiringManagers] = useState([]);
    // V1.5.5 Phase 2 — locations + SPOCs for the currently-selected client
    // inside the New Requirement form. Fetched lazily whenever `form.client_id`
    // changes so recruiters see the client's offices auto-materialize.
    const [clientLocations, setClientLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [filterClient, setFilterClient] = useState("");
    // V1.5.7.1 — Pre-fill filters from URL query params so the LandingPage
    // hero search (which navigates to /jobs?location=…&q=…) actually filters.
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterLocation, setFilterLocation] = useState(() => searchParams.get("location") || "");
    // Batch 3a (Feb 2026) — Requirement filters: status (default Open),
    // location, and age-bucket (days since created_at).
    const [filterStatus, setFilterStatus] = useState("Open");
    const [filterAgeBucket, setFilterAgeBucket] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(
        () =>
            api
                .get("/jobs", { params: filterClient ? { client_id: filterClient } : {} })
                .then((r) => setJobs(r.data))
                .catch((e) => logError("Failed to load jobs:", e)),
        [filterClient],
    );
    useEffect(() => {
        load();
    }, [load]);
    // V1.5.7.1 — Keep the URL in sync with the location filter so users can
    // share/deep-link a filtered view AND the LandingPage hero navigation lands
    // on a correctly pre-filtered page. Runs after mount to avoid clobbering
    // any additional params other features may add later.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (filterLocation) next.set("location", filterLocation);
        else next.delete("location");
        // Avoid noisy re-renders when nothing changed.
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterLocation]);
    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api
            .get("/users/hiring-managers")
            .then((r) => setHiringManagers(r.data))
            .catch(() => {});
    }, []);

    // V1.5.5 Phase 2 — When the recruiter picks a Client in the New
    // Requirement form, fetch that client's locations + SPOCs and
    // auto-select the primary location + primary SPOC. If the client
    // has no locations, we clear both fields silently (falls back to
    // free-text `location` input the way it worked before).
    useEffect(() => {
        if (!showForm) return;   // only fetch while the modal is open
        if (!form.client_id) {
            setClientLocations([]);
            setForm((f) => ({ ...f, primary_location_id: "", primary_spoc_id: "" }));
            return;
        }
        let cancelled = false;
        setLocationsLoading(true);
        api
            .get(`/clients/${form.client_id}/locations`)
            .then((r) => {
                if (cancelled) return;
                const locs = r.data || [];
                setClientLocations(locs);
                // Auto-select the primary location if we don't have one
                // picked yet (or if the previous pick belonged to a different
                // client). Also auto-select the primary SPOC of that location.
                setForm((f) => {
                    const stillValid = locs.some((l) => l.id === f.primary_location_id);
                    if (stillValid) return f;
                    const primary = locs.find((l) => l.is_primary) || locs[0];
                    if (!primary) {
                        return { ...f, primary_location_id: "", primary_spoc_id: "" };
                    }
                    const primarySpoc =
                        (primary.spocs || []).find((s) => s.is_primary) ||
                        (primary.spocs || [])[0];
                    return {
                        ...f,
                        primary_location_id: primary.id,
                        primary_spoc_id: primarySpoc ? primarySpoc.id : "",
                        // Backfill the free-text `location` field with the
                        // location's city so it renders on job cards without
                        // requiring the recruiter to type it again.
                        location: f.location || primary.city || primary.label || "",
                    };
                });
            })
            .catch(() => {
                if (cancelled) return;
                setClientLocations([]);
            })
            .finally(() => {
                if (!cancelled) setLocationsLoading(false);
            });
        return () => { cancelled = true; };
    }, [form.client_id, showForm]);

    // Currently-selected location object (for the read-only preview card).
    const selectedLocation = React.useMemo(
        () => clientLocations.find((l) => l.id === form.primary_location_id) || null,
        [clientLocations, form.primary_location_id],
    );
    const selectedLocationSpocs = selectedLocation?.spocs || [];

    // Batch 3a — client-side filter application.
    // V1.5.5.16 — Canonical-only Location filter. Previously the dropdown
    // was polluted with recruiter free-text variants ("Bangalore Whitefield",
    // "Delhi NCR", "Mumbai Ghansoli", …) alongside the canonical cities.
    // Now we only surface the linked office cities (already normalised
    // to a canonical IN_CITIES entry) + any free-text values whose exact
    // string ALREADY matches a known IN_CITIES entry. Everything else
    // gets rolled up under the closest canonical city via substring match
    // in the filter predicate below, so no job is unreachable.
    const CANON_SET = React.useMemo(() => new Set(IN_CITIES.map((c) => c.toLowerCase())), []);
    const locations = React.useMemo(() => {
        const seen = new Set();
        const result = [];
        const canonicalise = (raw) => {
            const v = (raw || "").trim();
            if (!v) return null;
            // Exact canonical match — keep the recruiter-typed casing.
            if (CANON_SET.has(v.toLowerCase())) return v;
            // Substring match — "Bangalore Whitefield" → "Bangalore".
            const lc = v.toLowerCase();
            for (const c of IN_CITIES) {
                if (lc.includes(c.toLowerCase())) return c;
            }
            return null;
        };
        for (const j of jobs) {
            const linked = canonicalise(j.primary_location?.city);
            if (linked && !seen.has(linked)) { seen.add(linked); result.push(linked); }
            const free = canonicalise(j.location);
            if (free && !seen.has(free)) { seen.add(free); result.push(free); }
        }
        return result.sort();
    }, [jobs, CANON_SET]);

    const filteredJobs = React.useMemo(() => {
        const now = Date.now();
        const flc = (filterLocation || "").toLowerCase();
        return jobs.filter((j) => {
            // Status
            if (filterStatus !== "all" && (j.status || "Open") !== filterStatus) return false;
            // Location — canonical filter matches BOTH the linked office city
            // AND any free-text `location` that contains the canonical name
            // as a substring. So picking "Bangalore" surfaces jobs whose
            // recruiter typed "Bangalore Whitefield" too.
            if (filterLocation) {
                const linked = (j.primary_location?.city || "").toLowerCase();
                const freeText = (j.location || "").toLowerCase();
                if (linked !== flc && !freeText.includes(flc)) return false;
            }
            // Age bucket
            if (filterAgeBucket) {
                const created = j.created_at ? new Date(j.created_at).getTime() : now;
                const days = Math.floor((now - created) / 86400000);
                if (filterAgeBucket === "0-30" && !(days >= 0 && days < 30)) return false;
                if (filterAgeBucket === "30-60" && !(days >= 30 && days < 60)) return false;
                if (filterAgeBucket === "60-90" && !(days >= 60 && days < 90)) return false;
                if (filterAgeBucket === "90+" && !(days >= 90)) return false;
            }
            return true;
        });
    }, [jobs, filterStatus, filterLocation, filterAgeBucket]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            // Only send SPOCs that have a name — empty rows would 422 on the server.
            const spocs = (form.spocs || [])
                .filter((s) => (s.name || "").trim())
                .map((s) => ({
                    name: s.name.trim(),
                    email: (s.email || "").trim() || null,
                    phone: (s.phone || "").trim() || "",
                }));
            const payload = {
                ...form,
                client_id: form.client_id || null,
                hiring_manager_id: form.hiring_manager_id || null,
                primary_location_id: form.primary_location_id || null,
                primary_spoc_id: form.primary_spoc_id || null,
                work_mode: form.work_mode || "Onsite",
                screening_pass_threshold: Number(form.screening_pass_threshold) || 3.5,
                spocs,
                referral_eligible: !!form.referral_eligible,
                referral_bounty_amount: form.referral_eligible
                    ? Number(form.referral_bounty_amount) || 0
                    : null,
                referral_bounty_currency: form.referral_eligible
                    ? form.referral_bounty_currency || "INR"
                    : null,
            };
            await api.post("/jobs", payload);
            setForm(empty);
            setShowForm(false);
            load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const addSpoc = () => {
        setForm((f) => ({
            ...f,
            spocs: [...(f.spocs || []), { name: "", email: "", phone: "" }],
        }));
    };
    const updateSpoc = (idx, field, value) => {
        setForm((f) => ({
            ...f,
            spocs: (f.spocs || []).map((s, i) =>
                i === idx ? { ...s, [field]: value } : s,
            ),
        }));
    };
    const removeSpoc = (idx) => {
        setForm((f) => ({
            ...f,
            spocs: (f.spocs || []).filter((_, i) => i !== idx),
        }));
    };

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] mx-auto" data-testid="jobs-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Hiring Requirements
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Requirements.
                    </h1>
                </div>
                {canEditJob && (
                    <button
                        data-testid="open-new-job-button"
                        onClick={() => setShowForm(true)}
                        className="self-start sm:self-auto bg-cobalt hover:bg-cobalt-dark text-white px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                    >
                        <Plus size={14} weight="bold" /> New requirement
                    </button>
                )}
            </div>

            {/* Batch 3a (Feb 2026) — unified compact filter bar.
                Consolidates client + status + location + age into one row
                of consistent <select> dropdowns to reduce vertical footprint. */}
            <div
                data-testid="requirement-filters"
                className="flex flex-wrap items-center gap-2 mb-6"
            >
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mr-1 shrink-0">
                    Filters
                </span>
                {clients.length > 0 && (
                    <select
                        data-testid="filter-client"
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                        className="text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none px-2.5 h-8 bg-white cursor-pointer min-w-[110px]"
                    >
                        <option value="">All clients</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                )}
                <select
                    data-testid="filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none px-2.5 h-8 bg-white cursor-pointer min-w-[110px]"
                >
                    <option value="Open">Open</option>
                    <option value="Paused">Paused</option>
                    <option value="Closed">Closed</option>
                    <option value="Inactive">Inactive</option>
                    <option value="all">All statuses</option>
                </select>
                <select
                    data-testid="filter-location"
                    value={filterLocation}
                    onChange={(e) => setFilterLocation(e.target.value)}
                    className="text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none px-2.5 h-8 bg-white cursor-pointer min-w-[130px]"
                >
                    <option value="">All locations</option>
                    {locations.map((l) => (
                        <option key={l} value={l}>{l}</option>
                    ))}
                </select>
                <select
                    data-testid="filter-age-bucket"
                    value={filterAgeBucket}
                    onChange={(e) => setFilterAgeBucket(e.target.value)}
                    className="text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none px-2.5 h-8 bg-white cursor-pointer min-w-[110px]"
                >
                    <option value="">Any age</option>
                    <option value="0-30">0–30 days</option>
                    <option value="30-60">30–60 days</option>
                    <option value="60-90">60–90 days</option>
                    <option value="90+">90+ days</option>
                </select>
                {(filterStatus !== "Open" || filterLocation || filterAgeBucket || filterClient) && (
                    <button
                        data-testid="filter-reset"
                        onClick={() => {
                            setFilterStatus("Open");
                            setFilterLocation("");
                            setFilterAgeBucket("");
                            setFilterClient("");
                        }}
                        className="text-[11px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                    >
                        Reset
                    </button>
                )}
                <span
                    data-testid="requirement-filter-count"
                    className="text-[11px] text-[#A3A3A3] ml-auto"
                >
                    {filteredJobs.length} of {jobs.length}
                </span>
            </div>

            {jobs.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-16 text-center">
                    <Briefcase size={36} weight="bold" className="mx-auto text-[#A3A3A3] mb-4" />
                    <p className="font-heading text-2xl font-bold tracking-tight mb-2">
                        No requirements yet
                    </p>
                    <p className="text-[#525252] mb-6">
                        {canEditJob
                            ? "Create your first job requirement to start tracking candidates."
                            : "Your admin hasn't posted any requirements yet."}
                    </p>
                    {canEditJob && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-cobalt hover:bg-cobalt-dark text-white px-6 py-3 font-bold tracking-wide text-sm inline-flex items-center gap-2 transition-colors"
                        >
                            <Plus size={14} weight="bold" /> Create requirement
                        </button>
                    )}
                </div>
            ) : filteredJobs.length === 0 ? (
                <div
                    data-testid="requirements-empty-filtered"
                    className="bg-white border border-[#E5E5E5] p-16 text-center"
                >
                    <Briefcase size={36} weight="bold" className="mx-auto text-[#A3A3A3] mb-4" />
                    <p className="font-heading text-2xl font-bold tracking-tight mb-2">
                        No matches for these filters.
                    </p>
                    <p className="text-[#525252]">
                        Try switching status, location, or age bucket — or hit Reset above.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredJobs.map((j) => (
                        <Link
                            key={j.id}
                            to={`/requirements/${j.id}`}
                            data-testid={`job-card-${j.id}`}
                            className="bg-white border border-[#E5E5E5] hover:-translate-y-0.5 hover:border-ink transition-all p-6 group"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        {j.client?.name || j.department || "General"}
                                    </span>
                                    {j.code && (
                                        <span
                                            data-testid={`job-card-code-${j.id}`}
                                            className="font-mono text-[9px] tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-0.5"
                                        >
                                            {j.code}
                                        </span>
                                    )}
                                </div>
                                <span
                                    className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${
                                        j.status === "Open"
                                            ? "bg-[#ECFDF5] text-[#059669]"
                                            : j.status === "Paused"
                                              ? "bg-[#EFF6FF] text-[#F59E0B]"
                                              : "bg-[#F5F5F5] text-[#525252]"
                                    }`}
                                >
                                    {j.status}
                                </span>
                            </div>
                            <h3 className="font-heading text-xl font-bold tracking-tight mb-2 text-ink">
                                {j.title}
                            </h3>
                            <p className="text-sm text-[#525252] mb-4">
                                {(j.primary_location?.city || j.location || "—")} · {j.work_mode || j.employment_type || "Onsite"} · {j.employment_type} · {j.seniority}
                            </p>
                            <div className="pt-4 border-t border-[#E5E5E5] flex items-center justify-between">
                                <span className="text-xs font-bold text-[#525252]">
                                    {j.candidate_count} candidates
                                </span>
                                {j.salary_range && (
                                    <span className="text-xs font-bold text-ink">
                                        {typeof j.salary_range === "string"
                                            ? j.salary_range
                                            : (() => {
                                                const s = j.salary_range;
                                                const cur = s?.currency || "";
                                                const min = s?.min ?? "";
                                                const max = s?.max ?? "";
                                                if (min && max) return `${cur} ${min}-${max}`.trim();
                                                if (min) return `${cur} ${min}+`.trim();
                                                if (max) return `${cur} ${max}`.trim();
                                                return null;
                                            })()}
                                    </span>
                                )}
                            </div>
                            {(j.assigned_recruiters || []).length > 0 && (
                                <div
                                    data-testid={`job-card-assigned-${j.id}`}
                                    className="pt-3 mt-3 border-t border-dashed border-[#E5E5E5] flex flex-wrap items-center gap-1"
                                >
                                    <span className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        Assigned:
                                    </span>
                                    {j.assigned_recruiters.slice(0, 3).map((r) => (
                                        <span
                                            key={r.id}
                                            className="text-[10px] font-medium px-1.5 py-0.5 bg-[#EFF6FF] text-[#1E40AF]"
                                            title={r.email}
                                        >
                                            {r.name.split(" ")[0]}
                                        </span>
                                    ))}
                                    {j.assigned_recruiters.length > 3 && (
                                        <span className="text-[10px] text-[#525252]">
                                            +{j.assigned_recruiters.length - 3}
                                        </span>
                                    )}
                                </div>
                            )}
                        </Link>
                    ))}
                </div>
            )}

            {showForm && (
                <div
                    className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        data-testid="new-job-form"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-[#E5E5E5] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">
                                New requirement
                            </h2>
                            <button
                                onClick={() => setShowForm(false)}
                                data-testid="close-new-job-button"
                                className="p-2 hover:bg-[#F5F5F5]"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Client
                                </label>
                                <select
                                    data-testid="job-client_id-select"
                                    value={form.client_id || ""}
                                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                >
                                    <option value="">— Internal / no client —</option>
                                    {clients
                                        .filter((c) => c.status !== "archived")
                                        .map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                </select>
                                {!form.client_id && (
                                    <p
                                        data-testid="job-no-client-warning"
                                        className="mt-2 text-[11px] leading-relaxed text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] p-2"
                                    >
                                        Heads up: without a client, testimonials,
                                        white-label career pages, and share links won&apos;t
                                        work for this requirement. You can add a client
                                        later via Edit.
                                    </p>
                                )}
                            </div>

                            {/* V1.5.5 Phase 2 — Client Location + SPOC picker.
                                Auto-populates from the client's Locations/SPOCs
                                configured on the Client form. */}
                            {form.client_id && (
                                <div
                                    data-testid="job-location-spoc-block"
                                    className="bg-[#F8FAFC] border border-[#E5E5E5] p-4 space-y-3"
                                >
                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                                        <MapPin size={12} weight="fill" className="text-cobalt" />
                                        Client office & SPOC
                                    </div>

                                    {locationsLoading ? (
                                        <p className="text-[11px] text-[#A3A3A3]">Loading client locations…</p>
                                    ) : clientLocations.length === 0 ? (
                                        <p
                                            data-testid="job-no-locations-hint"
                                            className="text-[11px] leading-relaxed text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] p-2"
                                        >
                                            This client has no offices set up yet. Add
                                            locations and SPOCs on the Client page so
                                            you can attach a physical office and hiring
                                            contact to this requirement.
                                        </p>
                                    ) : (
                                        <>
                                            <div>
                                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5 block">
                                                    Office location
                                                </label>
                                                <select
                                                    data-testid="job-primary_location_id-select"
                                                    value={form.primary_location_id || ""}
                                                    onChange={(e) => {
                                                        const lid = e.target.value;
                                                        const loc = clientLocations.find((l) => l.id === lid);
                                                        const spoc =
                                                            loc && ((loc.spocs || []).find((s) => s.is_primary) || (loc.spocs || [])[0]);
                                                        setForm((f) => ({
                                                            ...f,
                                                            primary_location_id: lid,
                                                            primary_spoc_id: spoc ? spoc.id : "",
                                                            location: loc ? (loc.city || loc.label || f.location) : f.location,
                                                        }));
                                                    }}
                                                    className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                                >
                                                    <option value="">— Select office —</option>
                                                    {clientLocations.map((l) => {
                                                        const sameCity = (l.city || "").trim().toLowerCase() === (l.label || "").trim().toLowerCase();
                                                        return (
                                                            <option key={l.id} value={l.id}>
                                                                {l.label}
                                                                {l.city && !sameCity ? ` · ${l.city}` : ""}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>

                                            {selectedLocation && (
                                                <div
                                                    data-testid="job-selected-location-preview"
                                                    className="border border-[#E5E5E5] bg-white p-3"
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <MapPin size={14} weight="fill" className="text-cobalt mt-0.5 shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-ink flex items-center gap-2">
                                                                {selectedLocation.label}
                                                                {selectedLocation.is_primary && (
                                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                                                        <Star size={9} weight="fill" /> Primary
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <p className="text-[11px] text-[#525252] mt-0.5">
                                                                {selectedLocation.address_line_1}
                                                                {selectedLocation.address_line_2 ? `, ${selectedLocation.address_line_2}` : ""}
                                                                {selectedLocation.city ? `, ${selectedLocation.city}` : ""}
                                                                {selectedLocation.state ? `, ${selectedLocation.state}` : ""}
                                                                {selectedLocation.pincode ? ` ${selectedLocation.pincode}` : ""}
                                                            </p>
                                                            {selectedLocation.google_maps_url && (
                                                                <a
                                                                    href={selectedLocation.google_maps_url}
                                                                    target="_blank" rel="noopener noreferrer"
                                                                    data-testid="job-location-maps-link"
                                                                    className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-cobalt hover:underline"
                                                                >
                                                                    Open in Google Maps ↗
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {selectedLocationSpocs.length > 0 ? (
                                                        <div className="mt-3 pt-3 border-t border-[#F5F5F5]">
                                                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5 block">
                                                                Point of contact (SPOC)
                                                            </label>
                                                            <select
                                                                data-testid="job-primary_spoc_id-select"
                                                                value={form.primary_spoc_id || ""}
                                                                onChange={(e) =>
                                                                    setForm({ ...form, primary_spoc_id: e.target.value })
                                                                }
                                                                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                                            >
                                                                <option value="">— No SPOC —</option>
                                                                {selectedLocationSpocs.map((s) => (
                                                                    <option key={s.id} value={s.id}>
                                                                        {s.name} · {s.designation}
                                                                        {s.is_primary ? "  (Primary)" : ""}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {form.primary_spoc_id && (() => {
                                                                const spoc = selectedLocationSpocs.find((s) => s.id === form.primary_spoc_id);
                                                                if (!spoc) return null;
                                                                return (
                                                                    <div
                                                                        data-testid="job-selected-spoc-preview"
                                                                        className="mt-2 text-[11px] text-[#525252] flex flex-wrap items-center gap-3 pl-1"
                                                                    >
                                                                        <span className="inline-flex items-center gap-1 font-bold text-ink">
                                                                            <UserIcon size={11} weight="fill" /> {spoc.name}
                                                                        </span>
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Phone size={10} weight="fill" /> {spoc.phone}
                                                                        </span>
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Envelope size={10} weight="fill" /> {spoc.email}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    ) : (
                                                        <p className="mt-2 pt-2 border-t border-[#F5F5F5] text-[10px] text-[#A3A3A3]">
                                                            No SPOCs configured for this office yet.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Hiring manager
                                </label>
                                <select
                                    data-testid="job-hiring_manager_id-select"
                                    value={form.hiring_manager_id || ""}
                                    onChange={(e) => setForm({ ...form, hiring_manager_id: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                >
                                    <option value="">— None —</option>
                                    {hiringManagers.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name} ({u.email})
                                        </option>
                                    ))}
                                </select>
                                <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                    Auto-invited to every interview scheduled for this requirement.
                                </p>
                            </div>
                            <div className="sm:col-span-2 bg-[#F8FAFC] border border-[#E5E5E5] p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                                        Screening auto-advance
                                    </label>
                                    <span
                                        data-testid="threshold-value"
                                        className="font-heading text-lg font-black text-cobalt"
                                    >
                                        {Number(form.screening_pass_threshold).toFixed(1)} / 5
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="1"
                                    max="5"
                                    step="0.5"
                                    data-testid="threshold-slider"
                                    value={form.screening_pass_threshold}
                                    onChange={(e) =>
                                        setForm({ ...form, screening_pass_threshold: e.target.value })
                                    }
                                    className="w-full accent-cobalt"
                                />
                                <div className="flex justify-between text-[10px] text-[#A3A3A3] mt-1">
                                    <span>1.0 (lenient)</span>
                                    <span>3.5 (default)</span>
                                    <span>5.0 (strict)</span>
                                </div>
                                <p className="text-[10px] text-[#525252] mt-2">
                                    Candidates whose average screening rating hits this score are
                                    automatically moved to the Interview stage.
                                </p>
                            </div>
                            {[
                                ["title", "Job title", "text", true],
                                ["department", "Department", "text", false],
                            ].map(([k, label, type, req]) => (
                                <div key={k}>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        {label}
                                    </label>
                                    <input
                                        data-testid={`job-${k}-input`}
                                        type={type}
                                        value={form[k]}
                                        required={req}
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Work mode
                                </label>
                                <select
                                    data-testid="job-work_mode-select"
                                    value={form.work_mode || "Onsite"}
                                    onChange={(e) => setForm({ ...form, work_mode: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                >
                                    <option value="Onsite">Onsite</option>
                                    <option value="Hybrid">Hybrid</option>
                                    <option value="Remote">Remote</option>
                                </select>
                                <p className="mt-1.5 text-[10px] text-[#A3A3A3]">
                                    Physical office comes from the linked Client Office above.
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Salary range
                                </label>
                                <AutocompleteInput
                                    testId="job-salary_range-input"
                                    field="salary_range"
                                    staticOptions={SALARY_SUGGESTIONS}
                                    value={form.salary_range}
                                    onChange={(v) => setForm({ ...form, salary_range: v })}
                                    placeholder="e.g. ₹15-25 LPA"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        Employment type
                                    </label>
                                    <select
                                        data-testid="job-employment_type-select"
                                        value={form.employment_type}
                                        onChange={(e) =>
                                            setForm({ ...form, employment_type: e.target.value })
                                        }
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                    >
                                        <option>Full-time</option>
                                        <option>Part-time</option>
                                        <option>Contract</option>
                                        <option>Internship</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        Seniority
                                    </label>
                                    <select
                                        data-testid="job-seniority-select"
                                        value={form.seniority}
                                        onChange={(e) => setForm({ ...form, seniority: e.target.value })}
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                    >
                                        <option>Junior</option>
                                        <option>Mid</option>
                                        <option>Senior</option>
                                        <option>Staff</option>
                                        <option>Lead</option>
                                        <option>Principal</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Job description
                                </label>
                                <textarea
                                    data-testid="job-description-input"
                                    value={form.description}
                                    required
                                    rows={5}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, description: v })))}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt whitespace-pre-wrap"
                                />
                                <p className="text-[10px] text-[#A3A3A3] mt-1">
                                    Tip: pasted text keeps its bullets, section headers, and line
                                    breaks automatically.
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Key requirements
                                </label>
                                <textarea
                                    data-testid="job-requirements-input"
                                    value={form.requirements}
                                    required
                                    rows={4}
                                    onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                                    onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, requirements: v })))}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt whitespace-pre-wrap"
                                />
                            </div>

                            {/* Extra SPOCs — one-off contacts specific to this
                                requirement. Primary contact comes from the
                                Client Office picker above (V1.5.5). */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] inline-flex items-center gap-1.5">
                                        <Users size={12} weight="bold" /> Extra SPOCs
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addSpoc}
                                        data-testid="add-spoc-button"
                                        className="text-[10px] font-black uppercase tracking-[0.15em] text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1"
                                    >
                                        <Plus size={11} weight="bold" /> Add extra SPOC
                                    </button>
                                </div>
                                {(form.spocs || []).length === 0 && (
                                    <p className="text-[11px] text-[#A3A3A3]">
                                        Primary contact is auto-picked from the linked Client Office. Add
                                        extra SPOCs only if there are additional stakeholders for this role.
                                    </p>
                                )}
                                <div className="space-y-2">
                                    {(form.spocs || []).map((s, idx) => (
                                        <div
                                            key={idx}
                                            data-testid={`spoc-row-${idx}`}
                                            className="grid grid-cols-1 sm:grid-cols-[1.1fr_1.3fr_1fr_auto] gap-2 items-start"
                                        >
                                            <input
                                                data-testid={`spoc-name-${idx}`}
                                                placeholder="Name"
                                                value={s.name}
                                                onChange={(e) => updateSpoc(idx, "name", e.target.value)}
                                                className="bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                            />
                                            <input
                                                data-testid={`spoc-email-${idx}`}
                                                type="email"
                                                placeholder="email@client.com"
                                                value={s.email}
                                                onChange={(e) => updateSpoc(idx, "email", e.target.value)}
                                                className="bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                            />
                                            <input
                                                data-testid={`spoc-phone-${idx}`}
                                                type="tel"
                                                placeholder="+91 98…"
                                                value={s.phone}
                                                onChange={(e) => updateSpoc(idx, "phone", e.target.value)}
                                                className="bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeSpoc(idx)}
                                                data-testid={`remove-spoc-${idx}`}
                                                aria-label="Remove SPOC"
                                                className="text-[#DC2626] hover:bg-[#FEF2F2] w-9 h-9 flex items-center justify-center border border-[#E5E5E5] hover:border-[#DC2626]"
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Per-job referral bounty override */}
                            <div className="border border-[#E5E5E5] p-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="referral-eligible-toggle"
                                        checked={!!form.referral_eligible}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                referral_eligible: e.target.checked,
                                            })
                                        }
                                        className="mt-1 accent-cobalt"
                                    />
                                    <div className="flex-1">
                                        <p className="font-bold text-ink text-sm">
                                            This role is referral-bounty eligible
                                        </p>
                                        <p className="text-[11px] text-[#525252] mt-0.5">
                                            When enabled, a Hired candidate on this job triggers the
                                            amount below as the referrer&apos;s payout (overrides the
                                            workspace default).
                                        </p>
                                    </div>
                                </label>
                                {form.referral_eligible && (
                                    <div
                                        data-testid="referral-bounty-fields"
                                        className="grid grid-cols-[1fr_120px] gap-2 mt-3 pl-7"
                                    >
                                        <input
                                            data-testid="referral-bounty-amount"
                                            type="number"
                                            min="0"
                                            step="100"
                                            placeholder="Bounty amount (e.g. 5000)"
                                            value={form.referral_bounty_amount}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    referral_bounty_amount: e.target.value,
                                                })
                                            }
                                            required
                                            className="bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                        />
                                        <select
                                            data-testid="referral-bounty-currency"
                                            value={form.referral_bounty_currency}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    referral_bounty_currency: e.target.value,
                                                })
                                            }
                                            className="bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                        >
                                            <option value="INR">INR ₹</option>
                                            <option value="USD">USD $</option>
                                            <option value="EUR">EUR €</option>
                                            <option value="GBP">GBP £</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {err && (
                                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                                    {err}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="border border-[#E5E5E5] hover:border-ink px-6 py-3 font-bold tracking-wide text-sm transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="job-submit-button"
                                    disabled={saving}
                                    className="flex-1 bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm transition-colors"
                                >
                                    {saving ? "Saving…" : "Create requirement"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
